/** VUEX module for state management in app **/
import { MembersApi } from '@/services/api-members.js';
import { MemberEmailAddressesApi } from '@/services/api-member-email-addresses.js';

// Local state -----------------------------------------------------
export const state = getDefaultState();

// Computed properties of the state --------------------------------
export const getters = {
  members: (state) => {
    return state.members.data || [];
  },
  pagination: (state) => {
    // destructure out the pagination data
    const { meta } = state.members;
    return meta;
  },
  memberById: (state, getters) => (id) => {
    return getters.members.find((member) => member.id === id);
  },
  memberIndexById: (state, getters) => (id) => {
    return getters.members.findIndex((member) => member.id === id);
  },
  memberExtraEmailIndexById: (state) => (emailId) => {
    return state.member.extra_email_addresses
      ? state.member.extra_email_addresses.findIndex((address) => address.id === emailId)
      : null;
  },
};

// Synchronous mutation of the local state -------------------------
export const mutations = {
  SET_MEMBERS(state, members) {
    state.members = members;
  },

  REPLACE_MEMBER_IN_LIST(state, { member, index }) {
    state.members.data.splice(index, 1, member);
  },

  UPDATE_MEMBER(state, member) {
    state.member = member;
  },

  /* *** MEMBER EMAIL ADDRESSES *** */

  SET_MEMBER_EMAIL_ADDRESSES(state, emails) {
    state.member = { ...state.member, extra_email_addresses: emails };
  },

  UPDATE_MEMBER_EMAIL_ADDRESSES(state, email) {
    state.member.extra_email_addresses.push(email);
  },

  REMOVE_MEMBER_EMAIL_ADDRESS(state, index = -1) {
    if (index !== -1) {
      state.member.extra_email_addresses.splice(index, 1);
    }
  },

  /* *** RESETS *** */

  RESET_STATE(state, defaultState) {
    state = defaultState;
  },
};

// Async CRUD actions against the api ------------------------------
export const actions = {
  /**
   * Fetch a list of members
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.query - filter query string
   * @returns {Promise}
   */
  index({ commit, state }, { query }) {
    return MembersApi.index(query).then((members) => {
      commit('SET_MEMBERS', members);
      return state.members;
    });
  },

  /**
   * Create a new individual member
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.data - form data
   * @returns {Promise}
   */
  store({ commit, state }, { data }) {
    return MembersApi.store(data).then((member) => {
      commit('UPDATE_MEMBER', member);
      return state.member;
    });
  },

  /**
   * Get details for a particular members
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.id - member id
   * @returns {Promise}
   */
  show({ commit, state, getters }, { id }) {
    // 1. Have we cached a matching member already
    let member = state.member;
    if (member.id !== id) {
      member = getters.memberById(id);
    }

    // 2. Make sure it has trips
    if (member && member.trips) {
      commit('UPDATE_MEMBER', member);
      return Promise.resolve(state.member);
    }

    // 3. or, Fetch the member with trips from the api
    return MembersApi.show(id).then((member) => {
      commit('UPDATE_MEMBER', member);
      return state.member;
    });
  },

  /**
   * Update a member's profile
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.id - member id
   * @param {String} payload.data - form data
   * @returns {Promise}
   */
  update({ commit }, { id, data }) {
    return MembersApi.update(id, data).then((member) => {
      commit('UPDATE_MEMBER', member);
    });
  },

  /**
   * Delete a particular member
   * @param {String} id - member id
   * @returns {Promise.<Object>}
   */
  destroy({ state, commit, getters }, id) {
    return MembersApi.destroy(id).then((member) => {
      replaceMember({ commit, getters, member, id });
      return state.member;
    });
  },

  /**
   * Deactivate a particular member
   * @param {String} id - member id
   * @returns {Promise.<Object>}
   */
  deactivate({ state, commit, getters }, id) {
    return MembersApi.deactivate(id).then((member) => {
      replaceMember({ commit, getters, member, id });
      return state.member;
    });
  },

  /**
   * Reactivate a particular member
   * @param {String} id - member id
   * @returns {Promise.<Object>}
   */
  reactivate({ state, commit, getters }, id) {
    return MembersApi.reactivate(id).then((member) => {
      replaceMember({ commit, getters, member, id });
      return state.member;
    });
  },

  /* *** MEMBER EMAIL ADDRESSES *** */

  /**
   * Get the list of email addresses associated to a particular member
   * @param {Object} context - vuex context
   * @param {String} id - member id
   * @returns {Promise}
   */
  indexAddress({ commit }, id) {
    return MemberEmailAddressesApi.index(id)
      .then((response) => {
        commit('SET_MEMBER_EMAIL_ADDRESSES', response);
      })
      .catch(() => {
        commit('SET_MEMBER_EMAIL_ADDRESSES', []);
      });
  },

  /**
   * Create another email address for a member
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.id - member id
   * @param {Object} payload.data - form data
   * @returns {Promise}
   */
  storeAddress({ commit }, { id, data }) {
    return MemberEmailAddressesApi.store(id, data).then((response) => {
      commit('UPDATE_MEMBER_EMAIL_ADDRESSES', response);
    });
  },

  /**
   * Delete a particular email address for a member
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.id - member id
   * @param {String} payload.emailId - Id of the address to delete
   * @returns {Promise}
   */
  destroyAddress({ commit, getters }, { id, emailId }) {
    return MemberEmailAddressesApi.destroy(id, emailId).then(() => {
      commit('REMOVE_MEMBER_EMAIL_ADDRESS', getters.memberExtraEmailIndexById(emailId));
    });
  },

  /* *** RESETS *** */

  /**
   * Resets the stored member to the empty state
   * @param {Object} context - vuex context
   * @returns {Promise}
   */
  resetMember({ commit }) {
    commit('UPDATE_MEMBER', {});
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', getDefaultState());
  },
};

/// ///

function getDefaultState() {
  return {
    members: {},
    member: {},
  };
}

function replaceMember({ commit, getters, member, id }) {
  let index = getters.memberIndexById(id);
  if (index > -1) {
    commit('REPLACE_MEMBER_IN_LIST', { member, index });
  }

  if (state.member.id === member.id) {
    commit('UPDATE_MEMBER', member);
  }
}
